'use client'

import { IconBook2 } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { analytics } from '@/lib/analytics'
import { completeOnboardingActivationForCurrentStory } from '@/lib/onboarding'
import { saveReaderSentence } from '@/lib/reader/saved-sentences'
import { isSentenceSaved } from '@/lib/study'
import { saveReaderSentenceToDefaultDeck } from '@/lib/save-to-study'
import { cn } from '@/lib/utils'
import type { ReaderBlock, ReaderContent } from '@/types/reader'

function getCurrentReaderHref() {
  if (typeof window === 'undefined') return null
  return `${window.location.pathname}${window.location.search}`
}

export function useReaderSentenceSave({
  block,
  content,
  sentenceText
}: {
  block: ReaderBlock
  content: ReaderContent
  sentenceText: string
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: saved = false, isLoading } = useQuery({
    queryKey: ['sentence-saved', user?.id, sentenceText],
    queryFn: () => {
      if (!user?.id) return false
      return isSentenceSaved(sentenceText)
    },
    enabled: Boolean(user?.id && sentenceText),
    staleTime: 1000 * 60 * 5
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        const isNew = saveReaderSentence(content, block, sentenceText)
        return { isNew, savedToStudy: false }
      }

      await saveReaderSentenceToDefaultDeck(user.id, {
        content,
        block,
        sentenceText,
        readerHref: getCurrentReaderHref()
      })
      const isNew = saveReaderSentence(content, block, sentenceText)
      return { isNew, savedToStudy: true }
    },
    onSuccess: ({ isNew, savedToStudy }) => {
      analytics.readerSentenceSaved(
        content.sourceType,
        Boolean(block.translation)
      )
      const onboardingState = completeOnboardingActivationForCurrentStory()
      if (onboardingState) {
        analytics.onboardingCompleted(onboardingState.recommendedStorySlug)
      }
      toast.success(
        savedToStudy
          ? isNew
            ? 'Sentence saved to Sentence Mining'
            : 'Sentence already saved to Sentence Mining'
          : isNew
            ? 'Sentence saved'
            : 'Sentence already saved'
      )
      if (savedToStudy && user?.id) {
        queryClient.setQueryData(
          ['sentence-saved', user.id, sentenceText],
          true
        )
        queryClient.invalidateQueries({ queryKey: ['sentence-saved', user.id] })
        queryClient.invalidateQueries({
          queryKey: ['all-collections', user.id]
        })
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save sentence'
      )
    }
  })

  return {
    user,
    saved,
    isLoading,
    isPending: mutation.isPending,
    saveSentence: () => mutation.mutateAsync()
  }
}

export function ReaderSaveSentenceButton({
  block,
  content,
  sentenceText,
  className,
  variant = 'outline',
  label = 'Save sentence'
}: {
  block: ReaderBlock
  content: ReaderContent
  sentenceText: string
  className?: string
  variant?: 'default' | 'outline' | 'ghost'
  label?: string
}) {
  const { user, saved, isLoading, isPending, saveSentence } =
    useReaderSentenceSave({
      block,
      content,
      sentenceText
    })

  if (!user?.id) {
    return (
      <LoginDialog>
        <Button className={cn('w-full', className)} variant={variant}>
          <IconBook2 className="h-4 w-4" />
          {label}
        </Button>
      </LoginDialog>
    )
  }

  return (
    <Button
      className={cn('w-full', className)}
      variant={saved ? 'outline' : variant}
      disabled={isLoading || isPending || saved}
      onClick={() => void saveSentence()}
    >
      <IconBook2 className="h-4 w-4" />
      {isPending ? 'Saving...' : saved ? 'Saved' : label}
    </Button>
  )
}
